export interface FieldIssue {
  field: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly issues: FieldIssue[];
  readonly details: unknown;

  constructor(status: number, code: string, message: string, issues: FieldIssue[] = [], details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.issues = issues;
    this.details = details ?? (issues.length > 0 ? issues : undefined);
  }
}

export const NETWORK_ERROR_MESSAGE = 'Nem sikerült elérni a szervert. Kérjük, ellenőrizze az internetkapcsolatot.';

export interface RefreshResult<TUser = unknown> {
  user: TUser;
  accessToken: string;
}

let accessToken: string | null = null;
let refreshPromise: Promise<RefreshResult | null> | null = null;
let onSessionLost: (() => void) | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

export const getAccessToken = (): string | null => accessToken;

export const setSessionLostHandler = (handler: (() => void) | null): void => {
  onSessionLost = handler;
};

const parseBody = async (response: Response): Promise<unknown> => {
  if (response.status === 204) return null;

  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const toApiError = (status: number, body: unknown): ApiError => {
  const error = (body as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
  const issues = Array.isArray(error?.details) ? (error.details as FieldIssue[]) : [];

  return new ApiError(status, error?.code ?? 'UNKNOWN', error?.message ?? 'Váratlan hiba történt.', issues, error?.details);
};

export const refreshSession = async <TUser = unknown>(): Promise<RefreshResult<TUser> | null> => {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });

      if (!response.ok) {
        setAccessToken(null);
        return null;
      }

      const body = (await parseBody(response)) as RefreshResult | null;

      if (!body?.accessToken) {
        setAccessToken(null);
        return null;
      }

      setAccessToken(body.accessToken);
      return body;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise as Promise<RefreshResult<TUser> | null>;
};

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  skipAuthRetry?: boolean;
}

export const apiRequest = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' };

    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

    return fetch(`/api${path}`, {
      method: options.method ?? 'GET',
      credentials: 'include',
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {})
    });
  };

  let response: Response;

  try {
    response = await send();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'NETWORK_ERROR', NETWORK_ERROR_MESSAGE);
  }

  const canRetry = !options.skipAuthRetry && !path.startsWith('/auth/');

  if (response.status === 401 && canRetry) {
    const refreshed = await refreshSession();

    if (refreshed) {
      try {
        response = await send();
      } catch {
        throw new ApiError(0, 'NETWORK_ERROR', NETWORK_ERROR_MESSAGE);
      }
    } else {
      onSessionLost?.();
    }
  }

  const body = await parseBody(response);

  if (!response.ok) throw toApiError(response.status, body);

  return body as T;
};
